/*
 * Editor do modo de acesso do hotspot, sempre de um equipamento.
 *
 * Nao ha heranca: o que esta aqui vale so para este MikroTik, e campo em branco
 * cai no padrao de fabrica. Por isso o placeholder de cada campo numerico mostra
 * o valor que entra em vigor se ele ficar vazio.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { AUTH_MODES, AUTH_MODE_DESCRIPTIONS, AUTH_MODE_LABELS } from '@hotspot/shared';
import type { AuthMode } from '@hotspot/shared';
import { ApiRequestError, apiRequest } from '../lib/api';
import type { HotspotConfigDto } from '../lib/types';
import { Alert, Button, Field, Spinner } from './ui';

/** Ordem de exibicao: do mais simples ao que depende de integracao externa. */
const MODOS: AuthMode[] = [AUTH_MODES.OPEN, AUTH_MODES.REGISTER, AUTH_MODES.HUBSOFT];

interface Props {
  mikrotikId: number;
  podeEscrever: boolean;
  aviso?: ReactNode;
  /**
   * Avisa o modo que valeria AGORA, considerando o rascunho ainda nao salvo.
   * Quem exibe a previa da tela do cliente precisa disso na hora do clique —
   * esperar o salvar faria a previa mostrar a tela errada enquanto se decide.
   */
  onModo?: (modo: string) => void;
}

type Rascunho = {
  authMode: string;
  sessionTimeoutMinutes: string;
  idleTimeoutMinutes: string;
  revisitGraceDays: string;
  rateLimit: string;
  redirectUrl: string;
};

const VAZIO: Rascunho = {
  authMode: '',
  sessionTimeoutMinutes: '',
  idleTimeoutMinutes: '',
  revisitGraceDays: '',
  rateLimit: '',
  redirectUrl: '',
};

function paraRascunho(dto: HotspotConfigDto): Rascunho {
  return {
    authMode: dto.authMode ?? '',
    sessionTimeoutMinutes: dto.sessionTimeoutMinutes?.toString() ?? '',
    idleTimeoutMinutes: dto.idleTimeoutMinutes?.toString() ?? '',
    revisitGraceDays: dto.revisitGraceDays?.toString() ?? '',
    rateLimit: dto.rateLimit ?? '',
    redirectUrl: dto.redirectUrl ?? '',
  };
}

/** Campo em branco vira null: o backend le null como "padrao de fabrica". */
function numeroOuNulo(valor: string): number | null {
  const limpo = valor.trim();
  if (!limpo) return null;
  const numero = Number(limpo);
  return Number.isFinite(numero) ? numero : null;
}

export function AccessModeEditor({ mikrotikId, podeEscrever, aviso, onModo }: Props) {
  const [config, setConfig] = useState<HotspotConfigDto | null>(null);
  const [rascunho, setRascunho] = useState<Rascunho>(VAZIO);
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [salvo, setSalvo] = useState(false);

  useEffect(() => {
    let ativo = true;

    setCarregando(true);
    apiRequest<HotspotConfigDto>(`/admin/hotspot-config?mikrotikId=${mikrotikId}`)
      .then((dto) => {
        if (!ativo) return;
        setConfig(dto);
        setRascunho(paraRascunho(dto));
      })
      .catch((caught: unknown) => {
        if (!ativo) return;
        setErro(caught instanceof ApiRequestError ? caught.displayMessage : 'Falha ao carregar');
      })
      .finally(() => {
        if (ativo) setCarregando(false);
      });

    return () => {
      ativo = false;
    };
  }, [mikrotikId]);

  /*
   * Guardado em ref para o aviso nao entrar nas dependencias do efeito: com um
   * callback recriado a cada render no pai, isso viraria laco infinito.
   */
  const aoModo = useRef(onModo);
  aoModo.current = onModo;

  useEffect(() => {
    if (!config) return;
    // Campo vazio cai no padrao de fabrica, que e o que o efetivo ja traz.
    aoModo.current?.(rascunho.authMode || config.effective.authMode);
  }, [config, rascunho.authMode]);

  async function salvar() {
    setSalvando(true);
    setErro(null);
    setSalvo(false);

    try {
      const atualizado = await apiRequest<HotspotConfigDto>(
        `/admin/hotspot-config?mikrotikId=${mikrotikId}`,
        {
          method: 'PUT',
          body: {
            authMode: rascunho.authMode || null,
            sessionTimeoutMinutes: numeroOuNulo(rascunho.sessionTimeoutMinutes),
            idleTimeoutMinutes: numeroOuNulo(rascunho.idleTimeoutMinutes),
            revisitGraceDays: numeroOuNulo(rascunho.revisitGraceDays),
            rateLimit: rascunho.rateLimit.trim() || null,
            redirectUrl: rascunho.redirectUrl.trim() || null,
          },
        },
      );
      setConfig(atualizado);
      setRascunho(paraRascunho(atualizado));
      setSalvo(true);
    } catch (caught) {
      setErro(caught instanceof ApiRequestError ? caught.displayMessage : 'Falha ao salvar');
    } finally {
      setSalvando(false);
    }
  }

  if (carregando) return <Spinner label="Carregando configuração..." />;
  if (!config) return <Alert>{erro ?? 'Não foi possível carregar a configuração.'}</Alert>;

  const efetivo = config.effective;
  const modoAtual = (rascunho.authMode || efetivo.authMode) as AuthMode;
  const cadastroAtivo = modoAtual === AUTH_MODES.REGISTER;
  /* Cadastro e HubSoft coletam dado pessoal, entao os dois precisam do aceite. */
  const exigeTermos = cadastroAtivo || modoAtual === AUTH_MODES.HUBSOFT;

  return (
    <div className="space-y-4">
      {aviso}
      {erro ? <Alert>{erro}</Alert> : null}
      {salvo ? <Alert tone="success">Configuração salva.</Alert> : null}

      <fieldset className="space-y-2" disabled={!podeEscrever}>
        <legend className="mb-1 text-sm font-medium text-slate-700">Modo de acesso</legend>

        {MODOS.map((modo) => (
          <OpcaoModo
            key={modo}
            valor={modo}
            selecionado={rascunho.authMode === modo}
            titulo={AUTH_MODE_LABELS[modo]}
            descricao={AUTH_MODE_DESCRIPTIONS[modo]}
            onSelect={setModo}
          />
        ))}
      </fieldset>

      {exigeTermos ? (
        <Alert tone="info">
          Este modo exige um texto de termos vigente — é o aceite que dá base legal para
          guardar o dado do cliente. Sem ele o portal recusa a conexão; confira em{' '}
          <Link to="/termos" className="font-semibold underline">
            Termos
          </Link>
          .
        </Alert>
      ) : null}

      {modoAtual === AUTH_MODES.HUBSOFT ? (
        <Alert tone="info">
          O acesso é liberado após confirmar o CPF na base do HubSoft. As credenciais da
          integração ficam em <strong>Integrações</strong>.
        </Alert>
      ) : null}

      <div className="grid gap-4 sm:grid-cols-2">
        <Field
          label="Tempo máximo por sessão (minutos)"
          type="number"
          min={5}
          max={1440}
          value={rascunho.sessionTimeoutMinutes}
          placeholder="sem limite"
          disabled={!podeEscrever}
          onChange={(e) => setCampo('sessionTimeoutMinutes', e.target.value)}
          hint="Em branco: sem limite de tempo. Com valor, a conexão cai ao fim de cada sessão e o cliente entra de novo pelo portal; o tempo não se acumula entre sessões."
        />

        <Field
          label="Desconectar por inatividade (minutos)"
          type="number"
          min={1}
          max={240}
          value={rascunho.idleTimeoutMinutes}
          placeholder={`padrão: ${efetivo.idleTimeoutMinutes}`}
          disabled={!podeEscrever}
          onChange={(e) => setCampo('idleTimeoutMinutes', e.target.value)}
          hint="Libera o IP de quem saiu sem desconectar."
        />

        {cadastroAtivo ? (
          <Field
            label="Dias sem repetir o cadastro"
            type="number"
            min={0}
            max={3650}
            value={rascunho.revisitGraceDays}
            placeholder={`padrão: ${efetivo.revisitGraceDays}`}
            disabled={!podeEscrever}
            onChange={(e) => setCampo('revisitGraceDays', e.target.value)}
            hint="Quem volta dentro do prazo só confirma o nome, sem preencher de novo. Use 0 para pedir sempre."
          />
        ) : null}

        <Field
          label="Limite de velocidade"
          value={rascunho.rateLimit}
          placeholder={efetivo.rateLimit ?? 'sem limite'}
          disabled={!podeEscrever}
          onChange={(e) => setCampo('rateLimit', e.target.value)}
          hint="Formato do RouterOS, ex.: 2M/10M (envio/recebimento). Vazio = sem limite."
        />

        <Field
          label="Redirecionar após conectar"
          value={rascunho.redirectUrl}
          placeholder={efetivo.redirectUrl ?? 'volta para o site que o cliente tentou abrir'}
          disabled={!podeEscrever}
          onChange={(e) => setCampo('redirectUrl', e.target.value)}
          hint="Deixe vazio para o cliente seguir para o endereço que ele pediu."
        />
      </div>

      {podeEscrever ? (
        <div className="flex justify-end">
          <Button onClick={() => void salvar()} disabled={salvando}>
            {salvando ? 'Salvando...' : 'Salvar'}
          </Button>
        </div>
      ) : null}
    </div>
  );

  function setCampo(chave: keyof Rascunho, valor: string) {
    setSalvo(false);
    setRascunho((atual) => ({ ...atual, [chave]: valor }));
  }

  function setModo(valor: string) {
    setCampo('authMode', valor);
  }
}

function OpcaoModo({
  valor,
  selecionado,
  titulo,
  descricao,
  onSelect,
}: {
  valor: string;
  selecionado: boolean;
  titulo: string;
  descricao: string;
  onSelect: (valor: string) => void;
}) {
  return (
    <label
      className={`flex cursor-pointer gap-3 rounded-lg border p-3 transition-colors ${
        selecionado ? 'border-brand-500 bg-brand-50' : 'border-slate-200 hover:bg-slate-50'
      }`}
    >
      <input
        type="radio"
        name="authMode"
        className="mt-1 size-4 shrink-0 accent-brand-600"
        checked={selecionado}
        value={valor}
        onChange={() => onSelect(valor)}
      />
      <span>
        <span className="block text-sm font-medium text-slate-800">{titulo}</span>
        <span className="block text-xs text-slate-600">{descricao}</span>
      </span>
    </label>
  );
}
